import React, { useState } from 'react';
import './Devis.css';

type Form = Record<string, string>;
type Errors = Record<string, string>;

const initialForm: Form = {
  Prénom: '',
  Nom: '',
  'Adresse E-mail': '',
  'Numéro de Téléphone': '',
  'Code Postal': '',
  'Message complémentaire': '',
  'Consentement RGPD': ''
};

const stepTitles: Record<number, string> = {
  1: 'Vos coordonnées',
  2: 'Votre message'
};

const stepFields: Record<number, string[]> = {
  1: ['Prénom', 'Nom', 'Adresse E-mail', 'Numéro de Téléphone', 'Code Postal'],
  2: ['Message complémentaire', 'Consentement RGPD']
};

const FORM_ENDPOINT = 'https://formspree.io/f/mzzyzkjq';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[0-9+().\s-]{6,20}$/;

export default function Devis() {
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState<Form>(initialForm);
  const [errors, setErrors] = useState<Errors>({});
  const [formError, setFormError] = useState('');
  const [submitError, setSubmitError] = useState('');

  const onChange = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setFormError('');
    setSubmitError('');
  };

  const validateFields = (fields: string[]): Errors => {
    const nextErrors: Errors = {};
    fields.forEach(key => {
      // Cas particulier RGPD (checkbox)
      if (key === 'Consentement RGPD') {
        if (!form['Consentement RGPD']) {
          nextErrors[key] = 'Vous devez accepter la politique de confidentialité.';
        }
        return;
      }

      const value = form[key]?.trim() ?? '';

      if (!value) {
        nextErrors[key] = 'Ce champ est obligatoire.';
        return;
      }

      if (key === 'Adresse E-mail') {
        if (!emailRegex.test(value)) {
          nextErrors[key] = 'Veuillez saisir une adresse e-mail valide.';
        }
      }

      if (key === 'Numéro de Téléphone') {
        if (!phoneRegex.test(value)) {
          nextErrors[key] = 'Veuillez saisir un numéro de téléphone valide.';
        }
      }
    });
    return nextErrors;
  };

  const validateAll = (): Errors => {
    const allRequired = [
      'Prénom',
      'Nom',
      'Adresse E-mail',
      'Numéro de Téléphone',
      'Code Postal',
      'Message complémentaire',
      'Consentement RGPD'
    ];
    return validateFields(allRequired);
  };

  const goToNextStep = (currentStep: number) => {
    const currentFields = stepFields[currentStep] || [];
    const stepErrors = validateFields(currentFields);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...stepErrors }));
      setFormError('Merci de corriger les champs indiqués en rouge.');
      return;
    }
    setFormError('');
    setStep(currentStep + 1);
  };

  const onSubmit: React.FormEventHandler = async e => {
    e.preventDefault();
    setFormError('');
    setSubmitError('');

    const newErrors = validateAll();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setFormError('Merci de vérifier les champs obligatoires avant l’envoi.');
      setStep(1);
      return;
    }

    setSending(true);
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          ...form,
          source: '+Propre site web',
          step
        })
      });
      if (!response.ok) {
        throw new Error('Request failed');
      }
      setSubmitted(true);
    } catch (err) {
      setSubmitError(
        "Une erreur est survenue lors de l'envoi. Veuillez réessayer dans quelques instants."
      );
    } finally {
      setSending(false);
    }
  };

  const resetForm = () => {
    setForm(initialForm);
    setStep(1);
    setSubmitted(false);
    setSending(false);
    setErrors({});
    setFormError('');
    setSubmitError('');
  };

  if (submitted) {
    return (
      <div className="devis-success">
        <div className="devis-success-inner">
          <div className="success-icon">
            <span className="checkmark">✓</span>
          </div>
          <h3>Votre demande de devis a été envoyée</h3>
          <p>Merci pour votre confiance. Nous vous répondrons dans les plus brefs délais.</p>
          <button type="button" className="btn secondary" onClick={resetForm}>
            Envoyer un autre devis
          </button>
        </div>
      </div>
    );
  }

  const progress = (step / 2) * 100;

  const getFieldClass = (key: string) => (errors[key] ? 'col field-error' : 'col');

  return (
    <section className="page devis-page">
      <div className="card devis-intro">
        <h2>Demande de devis</h2>
        <p className="muted devis-lead">
          Expliquez-nous votre besoin en quelques étapes. Plus les informations sont précises,
          plus nous pouvons vous proposer une estimation adaptée.
        </p>
        <div className="devis-highlights">
          <span className="devis-pill">Réponse rapide</span>
          <span className="devis-pill">Formulaire simplifié</span>
          <span className="devis-pill">Professionnels à votre service</span>
        </div>
      </div>

      <form className="frm multi-step-form" onSubmit={onSubmit} noValidate>
        <div className="devis-steps">
          <div className="devis-steps-header">
            <span className="devis-steps-count">Étape {step} sur 2</span>
            <span className="devis-steps-title">{stepTitles[step]}</span>
          </div>
          <div className="devis-steps-bar">
            <div className="devis-steps-bar-inner" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {step === 1 && (
          <fieldset className="card active">
            <legend>I. Vos coordonnées</legend>
            <div className="row">
              <label className={getFieldClass('Prénom')}>
                <span className="label-text">Prénom*</span>
                <input
                  required
                  value={form['Prénom']}
                  onChange={e => onChange('Prénom', e.target.value)}
                />
                {errors['Prénom'] && (
                  <div className="field-error-message">{errors['Prénom']}</div>
                )}
              </label>

              <label className={getFieldClass('Nom')}>
                <span className="label-text">Nom*</span>
                <input
                  required
                  value={form['Nom']}
                  onChange={e => onChange('Nom', e.target.value)}
                />
                {errors['Nom'] && <div className="field-error-message">{errors['Nom']}</div>}
              </label>

              <label className={getFieldClass('Adresse E-mail')}>
                <span className="label-text">Adresse E-mail*</span>
                <input
                  required
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={form['Adresse E-mail']}
                  onChange={e => onChange('Adresse E-mail', e.target.value)}
                />
                {errors['Adresse E-mail'] && (
                  <div className="field-error-message">{errors['Adresse E-mail']}</div>
                )}
              </label>

              <label className={getFieldClass('Numéro de Téléphone')}>
                <span className="label-text">Numéro de portable*</span>
                <input
                  required
                  inputMode="tel"
                  autoComplete="tel"
                  value={form['Numéro de Téléphone']}
                  onChange={e => onChange('Numéro de Téléphone', e.target.value)}
                />
                {errors['Numéro de Téléphone'] && (
                  <div className="field-error-message">{errors['Numéro de Téléphone']}</div>
                )}
              </label>

              <label className={getFieldClass('Code Postal')}>
                <span className="label-text">Code Postal*</span>
                <input
                  required
                  inputMode="numeric"
                  value={form['Code Postal']}
                  onChange={e => onChange('Code Postal', e.target.value)}
                />
                {errors['Code Postal'] && (
                  <div className="field-error-message">{errors['Code Postal']}</div>
                )}
              </label>
            </div>

            {formError && <div className="form-error-message">{formError}</div>}

            <div className="actions">
              <button
                type="button"
                className="btn primary"
                onClick={() => goToNextStep(1)}
              >
                Suivant →
              </button>
            </div>
          </fieldset>
        )}

        {step === 2 && (
          <fieldset className="card active">
            <legend>II. Votre message</legend>
            <div className="row">
              <label className="col col-span-2">
                <span className="label-text">Votre message*</span>
                <textarea
                  required
                  rows={5}
                  value={form['Message complémentaire']}
                  onChange={e => onChange('Message complémentaire', e.target.value)}
                />
                {errors['Message complémentaire'] && (
                  <div className="field-error-message">
                    {errors['Message complémentaire']}
                  </div>
                )}
              </label>
            </div>

            <div className={`rgpd-consent ${errors['Consentement RGPD'] ? 'field-error' : ''}`}>
              <label>
                <input
                  type="checkbox"
                  checked={!!form['Consentement RGPD']}
                  onChange={e =>
                    onChange('Consentement RGPD', e.target.checked ? 'oui' : '')
                  }
                />
                <span>
                  J&apos;accepte que mes données soient utilisées pour traiter ma demande,
                  conformément à la politique de confidentialité.*
                </span>
              </label>
              {errors['Consentement RGPD'] && (
                <div className="field-error-message">
                  {errors['Consentement RGPD']}
                </div>
              )}
            </div>

            {formError && <div className="form-error-message">{formError}</div>}
            {submitError && (
              <div className="form-error-message form-error-message-danger">
                {submitError}
              </div>
            )}

            <div className="actions">
              <button
                type="button"
                className="btn ghost"
                onClick={() => setStep(1)}
              >
                ← Précédent
              </button>
              <button
                type="submit"
                className="btn primary"
                disabled={sending}
              >
                {sending ? 'Envoi en cours…' : 'Envoyer ma demande'}
              </button>
            </div>
          </fieldset>
        )}
      </form>
    </section>
  );
}
